import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, HandledError } from '@synergy/api';
import { Dispatch } from 'redux';
import { FormikErrors } from 'formik';
import { Locale, Media } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { BankAccount } from '../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from '../../../../models/bank-account-form-values';
import { BlacklistedSupplier } from '../models/blacklisted-supplier';
import { CustomerDetail } from '../models/customer-detail';
import { CustomerDetailOverviewFormValues } from '../models/customer-detail-overview-form-values';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { SaveResult } from '../../../../../../common/models/save-result';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { customerDetailOverviewRequestSelector } from './customer-detail-overview-selectors';
import { getCountry } from '../../../../utils/get-country-values';
import { serverErrorNotification } from '../../../../../../common/utils/server-error-notification';
import { updateCustomerMember } from '../../store/customer-detail-actions';

export const CUSTOMER_DETAIL_BANK_ACCOUNT_ADD = 'CUSTOMER_DETAIL_BANK_ACCOUNT_ADD';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_CREATED = 'CUSTOMER_DETAIL_BANK_ACCOUNT_CREATED';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE = 'CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED = 'CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_UPDATED = 'CUSTOMER_DETAIL_BANK_ACCOUNT_UPDATED';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE = 'CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED = 'CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED';
export const CUSTOMER_DETAIL_OVERVIEW_ERROR = 'CUSTOMER_DETAIL_OVERVIEW_ERROR';
export const CUSTOMER_DETAIL_BANK_ACCOUNT_ERROR = 'CUSTOMER_DETAIL_BANK_ACCOUNT_ERROR';
export const CUSTOMER_DETAIL_OVERVIEW_RECEIVE_DETAILS = 'CUSTOMER_DETAIL_OVERVIEW_RECEIVE_DETAILS';
export const CUSTOMER_DETAIL_OVERVIEW_REQUEST_DETAILS = 'CUSTOMER_DETAIL_OVERVIEW_REQUEST_DETAILS';
export const CUSTOMER_DETAIL_OVERVIEW_SAVE = 'CUSTOMER_DETAIL_OVERVIEW_SAVE';
export const CUSTOMER_DETAIL_OVERVIEW_UPDATE = 'CUSTOMER_DETAIL_OVERVIEW_UPDATE';
export const CUSTOMER_DETAIL_UPLOAD_PROFILE_FINISHED = 'CUSTOMER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED';
export const CUSTOMER_DETAIL_UPLOAD_PROFILE_IMAGE = 'CUSTOMER_DETAIL_UPLOAD_PROFILE_IMAGE';
export const CUSTOMER_DETAIL_REMOVED_PROFILE_IMAGE = 'CUSTOMER_DETAIL_REMOVED_PROFILE_IMAGE';
export const CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_RECEIVE = 'CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_RECEIVE';
export const CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_REQUEST = 'CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_REQUEST';

const requestCustomer = () => {
    return {
        type: CUSTOMER_DETAIL_OVERVIEW_REQUEST_DETAILS,
    };
};

export const updateCustomerDetail = (
    formData: CustomerDetailOverviewFormValues,
    isDirty: boolean,
    isValid: boolean,
    errors: FormikErrors<CustomerDetailOverviewFormValues>
) => {
    return {
        type: CUSTOMER_DETAIL_OVERVIEW_UPDATE,
        formData,
        isDirty,
        isValid,
        errors,
    };
};

const overviewError = (errorData?: HandledError<EmailErrorCode>[]) => ({
    type: CUSTOMER_DETAIL_OVERVIEW_ERROR,
    errorData,
});

const bankAccountError = (errorData?: HandledError<BankAccountErrorCode>[]) => ({
    type: CUSTOMER_DETAIL_BANK_ACCOUNT_ERROR,
    errorData,
});

const receiveCustomerDetails = (customerDetails: CustomerDetail, locale: Locale) => {
    const customer = getCustomerFormValues(customerDetails, locale);

    return {
        type: CUSTOMER_DETAIL_OVERVIEW_RECEIVE_DETAILS,
        customer,
        bankAccounts: customerDetails.bankAccounts,
    };
};

const receiveNewBankAccount = (newBankAccount: BankAccount) => {
    return {
        type: CUSTOMER_DETAIL_BANK_ACCOUNT_CREATED,
        newBankAccount,
    };
};

const receiveUpdatedBankAccount = (bankAccount: BankAccount) => {
    return {
        type: CUSTOMER_DETAIL_BANK_ACCOUNT_UPDATED,
        bankAccount,
    };
};

const deleteBankAccountFinished = (id: number) => {
    return {
        type: CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
        id,
    };
};

const fileUploadFinished = (bankAccountId: number, media: Media) => {
    return {
        type: CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
        bankAccountId,
        media,
    };
};

const receiveBlacklistedSuppliers = (blacklistedSuppliers: BlacklistedSupplier[]) => {
    return {
        type: CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_RECEIVE,
        blacklistedSuppliers,
    };
};

const getCustomerFormValues = (customer: CustomerDetail, locale: Locale): CustomerDetailOverviewFormValues => {
    const addresses = customer.addresses.map(address => {
        return {
            ...address,
            country: getCountry(locale, address.country),
        };
    });

    const notifications =
        customer.notificationSettings?.map(notificationSetting => ({
            ...notificationSetting,
            overrideEmail: !!notificationSetting.recipientEmail,
            recipientEmail: notificationSetting.recipientEmail || '',
        })) ?? [];

    return {
        ...customer,
        addresses,
        notificationSettings: notifications,
    };
};

const profileUploadFinished = (media: Media) => {
    return {
        type: CUSTOMER_DETAIL_UPLOAD_PROFILE_FINISHED,
        media,
    };
};

const uploadNewFiles = (media: Media[], id: number) => {
    return (dispatch: Dispatch, getState: () => AppState, api: { api: Api }) => {
        const newFiles = media.reduce(
            (acc: File[], item: Media) => (item.file !== undefined ? [...acc, item.file] : acc),
            []
        );

        if (newFiles) {
            newFiles.forEach(file => {
                uploadFile(id, file)(dispatch, getState, api);
            });
        }
    };
};

export const fetchCustomerDetails = (customerId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestCustomer());
        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.get(customerId);
        if (response.success) {
            dispatch(receiveCustomerDetails(response.result, bestLocaleSelector(getState())));
            dispatch(updateCustomerMember(response.result.isMember, response.result.memberSince ?? null));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveCustomerDetail = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_OVERVIEW_SAVE });
        const request = customerDetailOverviewRequestSelector(getState());
        if (!request) {
            return SaveResult.Error;
        }
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;

        let response: ApiResult<CustomerDetail>;
        if (customerId) {
            response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.patchDetail(
                customerId,
                request
            );
        } else {
            response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.createDetail(request);
        }

        if (response.success) {
            dispatch(receiveCustomerDetails(response.result, bestLocaleSelector(getState())));
            return SaveResult.Success;
        }

        // The error handler answers with a list either way, so an empty one means the
        // rejection could not be pinned on a field. Reporting that as a form error
        // leaves the user with a red tab and nothing marked in it.
        if (response.handledErrors?.length) {
            dispatch(overviewError(response.handledErrors));
            return SaveResult.FormErrors;
        }

        dispatch(Snackbar.enqueue(serverErrorNotification(response.error)));

        return SaveResult.ErrorReported;
    };
};

export const addNewBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_BANK_ACCOUNT_ADD });

        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.addBankAccount(
            customerId,
            bankAccount
        );

        if (response.success) {
            dispatch(receiveNewBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountAdded')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const updateExistingBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        const id = bankAccount.id;
        if (!customerId || !id) {
            return;
        }

        bankAccount.media.filter(media => media.id > 0);
        delete bankAccount.id;
        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.updateBankAccount(
            customerId,
            bankAccount,
            id
        );

        if (response.success) {
            dispatch(receiveUpdatedBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountSaved')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const deleteBankAccount = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE });

        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.deleteBankAccount(
            customerId,
            id
        );

        if (response.success) {
            dispatch(deleteBankAccountFinished(id));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountDeleteSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const uploadFile = (id: number, file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE });

        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.uploadFile(
            customerId,
            id,
            file
        );

        if (response.success) {
            dispatch(fileUploadFinished(id, response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.fileUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.fileUploadError')));
        }
    };
};

export const fetchBlacklistedSuppliers = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_REQUEST });

        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.searchBlacklistedSuppliers(
            customerId,
            searchParams
        );

        if (response.success) {
            dispatch(receiveBlacklistedSuppliers(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const uploadProfileImage = (file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_UPLOAD_PROFILE_IMAGE });

        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.uploadProfileImage(
            customerId,
            file
        );

        if (response.success) {
            dispatch(profileUploadFinished(response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.logoUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.logoUploadError')));
        }
    };
};

export const removeProfileImage = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Overview.removeProfileImage(
            customerId
        );

        if (response.success) {
            dispatch({ type: CUSTOMER_DETAIL_REMOVED_PROFILE_IMAGE });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.logoRemoveSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.logoRemoveError')));
        }
    };
};
